import ServiceFactory from '../service_factory';
import {
    SDKInterface,
    ConnectConfiguration,
    GameObject,
    ComplexMatchDetails,
    PlayerOptResponse,
    FullGameDetails,
    GameMode
} from './blue_ribbon_api_interfaces';
import BaseObject from '../../base/base_object';
import {BlueRibbonExecutionMonitor} from './blue_ribbon_helpers';
import {BlueRibbonEventsDispatcher} from './event_handlers/blue_ribbon_events_dispatcher';
import {IBlueRibbonConnection} from './blue_ribbon_connection_interface';
import {IBlueRibbonEventsHandler} from './event_handlers/blue_ribbon_event_handler_interface';
import {BlueRibbonFeedsController} from './blue_ribbon_feeds_controller';
import Event from '../../utils/event';
import { SYSTEM_EVENTS } from '../../events/events';


export class BlueRibbonConnection extends BaseObject implements IBlueRibbonConnection {
    static CLASS = "BlueRibbonConnection";

    private readonly _operationExecutionMonitor: BlueRibbonExecutionMonitor;
    private readonly _blueRibbonEventsDispatcher: BlueRibbonEventsDispatcher = null;
    private readonly _feedsController: BlueRibbonFeedsController = null;
    private _connectionFailed: boolean = false;

    constructor(private readonly _blueRibbonCoreManager: SDKInterface,
                private readonly _serviceFactory: ServiceFactory,
                eventsHandlers: IBlueRibbonEventsHandler[]) {
        super();

        this._operationExecutionMonitor = new BlueRibbonExecutionMonitor(this._serviceFactory);
        this._blueRibbonEventsDispatcher = new BlueRibbonEventsDispatcher(_blueRibbonCoreManager.events, this._operationExecutionMonitor, eventsHandlers);
        this._feedsController = new BlueRibbonFeedsController(this._blueRibbonCoreManager, this._blueRibbonEventsDispatcher, this._operationExecutionMonitor);

        this._connectPromise = this._startConnection();
    }

    private _startConnection() {
        let connectOptions: ConnectConfiguration = {
            currency: this._serviceFactory.currencyService().getCurrency()
        };

        if(this._serviceFactory.oAuth2Service().isAuthorized()) {
            connectOptions.playerId = this._serviceFactory.userService().getCID().toString();
        }

        const executeConnect = () => this._blueRibbonCoreManager.connect(connectOptions).then(() => {
            this._isConnected = true;
            const matchedGames = this._blueRibbonCoreManager.operatorGames.getOperatorGamesMatchDetails().matchedOperatorGames as ComplexMatchDetails[] || [];
            this._blueRibbonEventsDispatcher.fireOnConnected(matchedGames);
        });

        return this._operationExecutionMonitor.execute({
                operationName: 'connect',
                operationParams: connectOptions,
                logExecutionTimeToElastic: true
            },
            executeConnect)
            .then(() => {
                if(this._isDisposed) { // no need to start all feeds if the connection was disposed meanwhile
                    return Promise.resolve();
                } else {
                    return this._feedsController.startAllFeeds().then(() => {
                        Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_CONNECTION_COMPLETE);
                        return Promise.resolve();
                    });
                }
            })
            .catch(err => {
                this._connectionFailed = true;
                throw err;
            });
    }

    private _isDisposed = false;
    dispose(): Promise<void> {
        this._isDisposed = true;
        const executeReset = () => this._blueRibbonCoreManager.reset().then(() => this._blueRibbonEventsDispatcher.dispose());

        return this.waitForConnect().then(() => {
            return this._operationExecutionMonitor.execute({
                operationName: 'reset'
            }, executeReset)
        }).catch(err => {
            // we don't want to fail the dispose because probably a new connect is going to happen
            this._operationExecutionMonitor.error('BlueRibbonConnection dispose failed', err);
        });
    }

    private _isConnected: boolean = false;
    public get isConnected(): boolean {
        return this._isConnected;
    }

    private _connectPromise: Promise<any> = null;

    public waitForConnect(): Promise<void> {
        if(this.isConnected) {
            return Promise.resolve();
        } else {
            if (this._connectionFailed) {
                this._connectionFailed = false;
                this._connectPromise = this._startConnection();
            }
            return this._connectPromise;
        }
    }

    getOperatorGamesMatchDetails(): Promise<ComplexMatchDetails[]> {
        return this.waitForConnect().then(() => {
           return this._blueRibbonCoreManager.operatorGames.getOperatorGamesMatchDetails({filter: 'WITH_COMPLEX_SEGMENTS'}).matchedOperatorGames as ComplexMatchDetails[] || [];
        });
    }

    isPlayerOptInToJackpotGame(game: GameObject): Promise<PlayerOptResponse> {
        return this.waitForConnect().then(() => {
            return this._blueRibbonCoreManager.player.isPlayerOptInToJackpotGame(game);
        });
    }

    getGameDetailsByGameIds(gameIds: GameObject[]): Promise<FullGameDetails[]> {
        const executeGetGameDetailsByGameIds = () => {
            return this.waitForConnect().then(() => this._blueRibbonCoreManager.ticker.getGameDetailsByGameIds(gameIds).then(response => response.gamesDetails));
        }

        return this._operationExecutionMonitor.execute({
                operationName: 'getGameDetailsByGameIds',
                operationParams: gameIds
            },
            executeGetGameDetailsByGameIds);
    }


    public async startOneJackpotFeed(jackpotId: string): Promise<void> {
        await this.waitForConnect();
        this._feedsController.stopOneJackpotFeed(jackpotId);
        await this._feedsController.startOneJackpotFeed(jackpotId, GameMode.IN_GAME);
    }

    public async stopOneJackpotFeed(jackpotId: string): Promise<void> {
        await this.waitForConnect();
        this._feedsController.stopOneJackpotFeed(jackpotId);
        await this._feedsController.startOneJackpotFeed(jackpotId, GameMode.LOBBY);
    }

}